import {
  brands,
  regular,
} from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import IconPerson from "@mui/icons-material/Person";
import Box from "@mui/material/Box";
import * as React from "react";
import { useIntercom } from "react-use-intercom";

import IconButtonWithDropdown from "../../common/IconButtonWithDropdown";
import { usePathPattern } from "../../hooks";
import { useBasicData, useShowSnackbar } from "../../hooks";
import { discordUrl, supportUrl } from "../../utils/constants";

type AppNavTopbarProps = {
  children: React.ReactNode;
};

export default function AppNavTopbar({ children }: AppNavTopbarProps) {
  const { user, userError } = useBasicData();
  const { showNewMessage } = useIntercom();
  const { showError } = useShowSnackbar();
  const pathPattern = usePathPattern();

  const buttonIsDark = /^:workspace\/canvas$/gm.test(pathPattern);

  if (userError) {
    showError(userError.message);
    return null;
  }

  const handleIntercomMessage = () => {
    showNewMessage();
  };

  const menuItems = [
    {
      id: "settings-memberships",
      name: "Manage Memberships",
      route: `/settings/workspaces`,
      icon: <FontAwesomeIcon icon={regular("buildings")} />,
      colors: "text-[inherit] bg-transparent",
    },
    {
      id: "settings-tokens",
      name: "Manage Access Tokens",
      route: `/settings/tokens`,
      icon: <FontAwesomeIcon icon={regular("share-nodes")} rotation={90} />,
      colors: "text-[inherit] bg-transparent",
      borderBottom: true,
    },
    {
      id: "support",
      name: "Layer Docs & FAQs",
      route: supportUrl,
      icon: <FontAwesomeIcon icon={regular("circle-question")} />,
      colors: "text-[inherit] bg-transparent",
      borderBottom: false,
    },
    {
      id: "message",
      name: "Contact Support",
      onClick: handleIntercomMessage,
      icon: (
        <FontAwesomeIcon
          icon={regular("message-lines")}
          className="translate-y-0.5"
        />
      ),
      colors: "text-[inherit] bg-transparent",
      borderBottom: true,
    },
    {
      id: "discord",
      name: "Join our Discord",
      route: discordUrl,
      icon: (
        <FontAwesomeIcon icon={brands("discord")} className="text-[0.8rem]" />
      ),
      colors: "text-[inherit] bg-transparent",
      borderBottom: true,
    },
    {
      id: "logout",
      name: "Log Out",
      route: `/logout`,
      icon: <FontAwesomeIcon icon={regular("arrow-right-from-bracket")} />,
      colors: "text-[inherit] bg-transparent",
      borderBottom: false,
    },
  ];

  return (
    <Box
      component="nav"
      className="fixed w-full h-16 z-[100] pointer-events-none"
    >
      <Box className="flex items-center justify-between w-full h-full p-4">
        <div className="flex justify-start w-full h-full">{children}</div>

        <div className="flex justify-end w-full h-full">
          <IconButtonWithDropdown
            className="flex items-center justify-center -mt-1 -mr-1 transition-all rounded-full pointer-events-auto h-9 w-9 ring-1 ring-inset ring-layer-600 bg-layer-900"
            enforceDarkMode={buttonIsDark}
            header={
              <header
                className={`${
                  user?.email ? "block" : "hidden"
                } w-[210px] px-4 pt-2 pb-3 mb-1.5 border-b border-layer-300 dark:border-layer-700 select-none`}
              >
                <div className="text-[0.6rem] uppercase tracking-wider font-semibold text-layer-600 dark:text-layer-300 mb-1">
                  Signed in as
                </div>
                <div className="flex items-center w-full space-x-3">
                  <picture className="flex items-center justify-center w-6 h-6 rounded-full shrink-0 bg-layer-300">
                    {user?.profilePicture ? (
                      <img
                        src={user.profilePicture}
                        className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full before:content-['👤'] before:text-base before:w-full before:h-full before:block before:bg-layer-300 before:grayscale before:text-center"
                      />
                    ) : (
                      <IconPerson fontSize="small" className="text-layer-700" />
                    )}
                  </picture>

                  <div className="flex-1 text-base truncate text-layer-700 dark:text-layer-200">
                    <div className="font-semibold truncate">{user.name}</div>
                    <div className="text-xs truncate text-layer-600 dark:text-layer-300">
                      {user.email}
                    </div>
                  </div>
                </div>
              </header>
            }
            links={menuItems}
            selectorPrefix="user-dropdown"
          >
            {user?.profilePicture ? (
              <img
                src={user.profilePicture}
                className="w-6 h-6 flex items-center justify-center rounded-full before:content-['👤'] before:text-base before:w-full before:h-full before:block before:bg-layer-600 before:grayscale before:text-center shadow-sm dark:shadow-black/50"
              />
            ) : (
              <IconPerson
                fontSize="small"
                className="shadow-sm group-hover:text-layer-900 dark:shadow-black/50"
              />
            )}
          </IconButtonWithDropdown>
        </div>
      </Box>
    </Box>
  );
}
